import React, { useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { analytics, useArticlesList } from '@dopomogai/agent-kit';
import { Server, Brain, Layout, Search, Hammer, Users } from 'lucide-react';

// --- COMPONENT IMPORTS ---
import { TopReferralBar } from '../top-referral/TopReferralBar';
import { HomePageHero } from '../hero/HomePageHero';
import { HeroVisual } from '../hero/HeroVisual';
import { LLMProvidersMarquee } from '../providers/LLMProvidersMarquee';
import { ProblemStatementSection } from '../problem/ProblemStatementSection';
import { WhyUsSection } from '../why-us/WhyUsSection'; 
import { ProcessPipelineSection } from '../process/ProcessPipelineSection';
import { UnitPricingSection } from '../pricing/UnitPricingSection';
import { ImpactParitySection } from '../impact/ImpactParitySection';
import { FAQSection } from '../faq/FAQSection';
import { FooterCTASection } from '../footer-cta/FooterCTASection';
import { ArticlesTeaserSection } from '../articles/ArticlesTeaserSection';
import { BookingSection } from '../booking/BookingSection';
import { CanvasOSCarousel } from '../why-us/CanvasOSCarousel';

// --- NEW FEATURE: THE ASSESSMENT ENGINE ---
import { AssessmentSection } from '../../features/assessment/AssessmentRoot'; 

export type HomePageCompositeProps = {
  onAction?: (id: string, payload?: any) => void;
  showChatbot?: boolean;
  chatbotProps?: any;
  cmsProps?: any;
};

export const HomePageComposite: React.FC<HomePageCompositeProps> = ({
  onAction = (id: string) => console.log('[Homepage action]', id),
  cmsProps,
}) => {
  const { t, i18n } = useTranslation('homepage');
  const p = cmsProps?.content?.props || cmsProps?.locales?.[i18n.language]?.props || {};

  // --- DATA FETCHING ---
  const { articles } = useArticlesList();
  
  const recentArticles = useMemo(() => {
    return articles.slice(0, 3).map(a => ({
      slug: a.slug,
      title: a.title,
      subtitle: a.subtitle,
      publishDate: a.publishDate,
      readTimeMinutes: a.readTimeMinutes,
      category: a.category,
      heroImage: a.heroImage
    }));
  }, [articles]);

  // Analytics
  useEffect(() => {
    analytics.trackEvent('homepage_v2_view');
  }, []);

  // --- HANDLERS ---
  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        analytics.trackEvent(`scroll_to_${id}`);
    }
  };

  const handleActionWrapper = (actionId: string, payload?: any) => {
    const assessmentActions = [
        'SCROLL_TRACKS', 
        'HERO_ASSESS', 
        'NAV_ASSESSMENT',
        'HERO_APPLY', 
        'AUDIT_APPLY',
        'PRICING_SOLO', 
        'PRICING_CORE', 
        'PRICING_VELOCITY'
    ];

    if (assessmentActions.includes(actionId)) {
        scrollToSection('assessment');
        return;
    }
    
    if (['SCROLL_BOOKING'].includes(actionId)) {
        scrollToSection('booking');
        return;
    }

    if (actionId === 'VIEW_ALL_ARTICLES') {
        onAction(actionId, { path: '/blog' });
        return;
    }
    if (actionId === 'OPEN_ARTICLE') {
        onAction(actionId, payload);
        return;
    }

    onAction(actionId, payload);
  };

  // --- CONTENT DEFINITIONS ---

  const solutionItems = useMemo(() => [
    { 
        icon: <Server size={32} className="text-slate-600" />, 
        title: "The Infrastructure", 
        text: "Private & Flexible. We provision a single-tenant, air-gapped environment that belongs to you. AWS, Digital Ocean, or On-Prem." 
    },
    { 
        icon: <Brain size={32} className="text-violet-600" />, 
        title: "The Brain", 
        text: "We ingest your Repos, Slack, and Docs. Your data is operated by Dedicated AI Workers who cite your specific business truth." 
    },
    { 
        icon: <Layout size={32} className="text-emerald-600" />, 
        title: "The Multiplayer Workspace", 
        text: "Think 'FigJam on Steroids' for building companies. Humans and Agents work side-by-side on an infinite canvas—from brainstorming strategy to executing code." 
    },
  ], []);

  const processSteps = useMemo(() => [
    { 
        phase: "Phase 1", 
        titleKey: "The Strategy Session", 
        textKey: "In a 45-minute deep-dive, we diagnose your bottlenecks and identify 'High-Leverage' automation targets. No sales pitch, just engineering reality.", 
        icon: <Search size={28} className="text-violet-700" />, 
        accentColor: 'violet' as const, 
        outputLabel: "Strategic Alignment" 
    },
    { 
        phase: "Phase 2", 
        titleKey: "The Foundation Sprint", 
        textKey: "We provision your Private Server, ingest your data into the Knowledge Graph, build your full AI blueprint and deploy your 'C-Suite Agents'. You can talk to your data immediately.", 
        icon: <Hammer size={28} className="text-blue-700" />, 
        accentColor: 'blue' as const, 
        outputLabel: "Live Infrastructure & Blueprint" 
    },
    { 
        phase: "Phase 3", 
        titleKey: "The Transformation", 
        textKey: "With the system live, we execute the deep workflow automation. Hire our Dedicated Unit to build the custom agents for you, or take the keys and build with your own team.", 
        icon: <Users size={28} className="text-emerald-700" />, 
        accentColor: 'emerald' as const, 
        outputLabel: "Autonomous Operations" 
    },
], []);

  const pricingTiers = useMemo(() => [
    { 
        name: "The Solo Unit", 
        price: "$6k / mo", 
        description: "For Startups & POCs. Includes 1 Dedicated Senior Engineer.", 
        teamSize: 1, 
        features: ["1 AI Transformation Lead", "Weekly Sprint Cycles", "Canvas OS License", "You Own the Asset", "Zero-Risk Talent Swap"], 
        cta: { label: "Book Strategy Session", actionId: "PRICING_SOLO" } 
    },
    { 
        name: "The Core Unit", 
        price: "$15k / mo", 
        description: "Standard Velocity. A full 3-person squad managed by us.", 
        highlight: true, 
        teamSize: 3, 
        features: ["1 Architect (Strategy)", "1 Builder (Code)", "1 Data Engineer (Context)", "Full 'Canvas OS' Setup", "You Own the Asset"], 
        cta: { label: "Book Strategy Session", actionId: "PRICING_CORE" } 
    },
    { 
        name: "The Velocity Unit", 
        price: "$24k / mo", 
        description: "Enterprise Velocity. Two squads operating in parallel.", 
        teamSize: 6, 
        features: ["Full 6-Person Squad", "Dedicated Project Manager", "Multi-Server Architecture", "SLA Guarantees", "Zero-Risk Talent Swap"], 
        cta: { label: "Book Strategy Session", actionId: "PRICING_VELOCITY" } 
    }
  ], []);

  return (
    <div className="bg-slate-50 min-h-screen font-sans">
      
      {/* --- TOP BAR --- */}
      <TopReferralBar
        text="Founding Partner Program — 3 slots this quarter"
        cta={{ label: "Check Readiness", actionId: "HERO_ASSESS" }}
        onAction={handleActionWrapper}
        accent="emerald"
      />

      {/* --- HERO --- */}
      <HomePageHero
        onAction={handleActionWrapper}
        customTitle={
          <div className="flex flex-col gap-2">
              <span className="text-slate-400 line-through decoration-rose-500 decoration-4 opacity-70 text-[0.8em]">
                  DON'T RENT AI.
              </span>
              <span className="text-slate-900">
                  OWN THE WORKFORCE.
              </span>
          </div>
        }
        subtitle={p['hero.subheadline'] || "Stop renting subscriptions. We build Sovereign AI Assets. We deploy a secure, autonomous workforce that operates on your infrastructure, not ours. You own the data, you own the code, and you keep the leverage forever."}
        heroVisual={<HeroVisual />}
        platformBadge="System Operational // Cohort Open"
        ethicalStickerText="3 Partner Slots Left"
        
        primaryCta={{ label: "Generate Your AI Roadmap", actionId: "SCROLL_TRACKS" }}
        
        featureStats={[
            { text: "Private & Sovereign", accent: "slate", icon: "shield" },
            { text: "You Own the Asset", accent: "emerald", icon: "bolt" },
            { text: "Human-in-the-Loop", accent: "blue", icon: "share" }
        ]}
        backgroundVariant="grid-blobs"
        backgroundIntensity="subtle"
        integrationsLineText="Connects with your stack"
        integrationsItems={[
            { label: "Slack", accent: "violet" },
            { label: "Jira", accent: "blue" },
            { label: "GitHub", accent: "slate" },
            { label: "Notion", accent: "slate" },
            { label: "Salesforce", accent: "blue" },
            { label: "HubSpot", accent: "orange" },
            { label: "Linear", accent: "violet" },
            { label: "PostgreSQL", accent: "blue" },
        ]}
      />

      {/* --- LOGO MARQUEE --- */}
      <LLMProvidersMarquee 
        items={[
            { name: 'OpenAI', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/openai_icon.svg'] },
            { name: 'Anthropic', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/anthropic.svg'] },
            { name: 'AWS Bedrock', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/aws%20logo.png'] }, 
            { name: 'Google Vertex', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/google.svg'] },
            { name: 'DeepSeek', srcs: ['https://registry.npmmirror.com/@lobehub/icons-static-png/latest/files/light/deepseek.png'] },
            { name: 'Mistral', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/mistral.svg'] },
            { name: 'Meta Llama', srcs: ['https://articles-dopomogai.sfo3.cdn.digitaloceanspaces.com/icons/meta.svg'] },
        ]} 
        size="xl"
        barPaddingY={40} 
        className="border-b border-slate-200 bg-white" 
      />

      {/* --- PROBLEM --- */}
      <div id="problem" className="scroll-mt-24">
        <ProblemStatementSection
           titleKey="problem.title"
           subtitleKey="problem.subtitle"
           textKey="problem.text"
           imageSrc="/images/illustrations/saas-anchor-4k.avif" 
        />
      </div>

      {/* --- SOLUTION (CANVAS OS) --- */}
      <div id="solution" className="scroll-mt-24">
        <WhyUsSection
          eyebrow="The Solution"
          title="Introducing 'Canvas OS'"
          subtitle="We don't sell a chatbot subscription. We build your Sovereign AI Infrastructure."
          items={solutionItems}
          columns={3}
          className="bg-white border-b-0"
        />
        <div className="bg-white pb-20 px-4 md:px-8">
            <CanvasOSCarousel />
        </div>
      </div>

      {/* --- ASSESSMENT ENGINE --- */}
      <AssessmentSection />

      {/* --- PROCESS --- */}
      <div id="process" className="scroll-mt-24">
        <ProcessPipelineSection
          titleKey="process.title"
          subtitleKey="process.subtitle"
          steps={processSteps}
          primaryCta={{ label: "Generate Your AI Roadmap", actionId: "SCROLL_TRACKS" }}
          onAction={handleActionWrapper}
        />
      </div>

      {/* --- PRICING (UPDATED TEXT) --- */}
      <div id="pricing" className="scroll-mt-24">
        <UnitPricingSection
           // UPDATED TITLE/SUBTITLE
           titleKey="pricing.title" // "Hire a Unit, Not a Freelancer." (From translation file or fallback)
           subtitleKey="pricing.subtitle" // We override via props logic inside UnitPricingSection, or update translation file.
           // NOTE: I am relying on the UnitPricingSection to use the updated fallback I wrote in the previous step.
           tiers={pricingTiers}
           onAction={handleActionWrapper}
        />
      </div>

      {/* --- IMPACT --- */}
      <div id="mission" className="scroll-mt-24">
        <ImpactParitySection
          onAction={handleActionWrapper}
        />
      </div>

      {/* --- BLOG TEASER --- */}
      <div id="blog" className="scroll-mt-24">
        <ArticlesTeaserSection 
            eyebrow="Engineering Log"
            title="Insights"
            subtitle="Technical deep dives into Agentic Infrastructure & LLM Architecture."
            articles={recentArticles}
            cta={{ label: "View All Insights", actionId: "VIEW_ALL_ARTICLES" }}
            onAction={handleActionWrapper}
            className="border-b-2 border-slate-200 bg-white"
        />
      </div>

      {/* --- FAQ --- */}
      <div id="faq" className="scroll-mt-24">
        <FAQSection
          title="Engineering FAQ"
          subtitle="Technical details about infrastructure, privacy, and the model."
          layout="grid"
          accent="emerald"
          items={[
            { 
              q: "What exactly do you deliver?", 
              a: "We provide the Infrastructure (The Private Server), the Software (Canvas OS), and the Labor (The Engineering Unit). You get a functioning, sovereign AI workforce, not just a login." 
            },
            { 
              q: "Why is this so expensive ($15k/mo)?", 
              a: "It is actually the most affordable path to AI. Hiring one Senior AI Engineer costs $180k/yr. We provide a full 3-person Unit for that price. Crucially, you only pay the subscription while we are actively building. Once your Core Brain is stable, you can pause the Unit and just pay for compute usage. You own the asset." 
            },
            { 
              q: "Where does the data live?", 
              a: "On your Private AI Server. This can be a single-tenant secure container (Digital Ocean/AWS) OR a self-hosted server on your premises. We never train on your data." 
            },
            { 
              q: "Can I use my own LLMs?", 
              a: "Yes. We are model-agnostic. We can connect to OpenAI/Anthropic via your keys, OR we can help you self-host Open Source models (like Llama 3 or DeepSeek) fine-tuned on your data." 
            },
            { 
              q: "Why is the Setup Sprint separate?", 
              a: "Because we cannot build a custom machine without measuring the room first. The Setup Sprint builds the Foundation (The Infrastructure & The Blueprint) that your AI-First company needs to exist." 
            },
            { 
              q: "What happens if I cancel?", 
              a: "You keep the machine. Unlike SaaS, we build a private asset for you. If you cancel, we hand over the admin keys to your Private Server. You own the code, the data, and the infrastructure forever." 
            }
          ]}
        />
        
        <div className="bg-white pb-16 text-center border-b-2 border-slate-200">
           <div 
             className="inline-flex items-center gap-2 text-slate-500 font-bold text-sm bg-slate-50 px-4 py-2 rounded-full border border-slate-200 hover:border-emerald-500 hover:text-emerald-700 transition-colors cursor-pointer"
             onClick={() => scrollToSection('booking')}
           >
              <span>Still have questions? <span className="underline font-extrabold">Schedule a call with founder</span></span>
           </div>
        </div>
      </div>

      {/* --- BOOKING --- */}
      <div id="booking" className="scroll-mt-24">
         <BookingSection 
            id="booking-calendar"
            title="Invite-Only Strategy Session"
            subtitle="We map your workflow friction and design your Autonomous Roadmap. See your future company before you build it."
            calLink="dopomogai/secret"
            calNamespace="secret"
            context={{
                title: "The 45-Minute Blueprint:",
                points: [
                    "Workflow Friction Analysis",
                    "Identification of High-Value 'Toil'",
                    "Architecture Blueprint Design",
                    "ROI Projection for 'The Unit'"
                ]
            }}
         />
      </div>

      {/* --- FOOTER CTA (UPDATED TEXT) --- */}
      <FooterCTASection
        title="Stop Renting Intelligence."
        subtitle="Start building assets. Generate your AI Roadmap today. If you qualify, you unlock a strategic blueprinting session to design your Company AI Computer."
        ctas={[{ label: "Generate Your AI Roadmap", actionId: "SCROLL_TRACKS", variant: "primary" }]}
        chips={["Private AI Server", "You Own the Asset", "No Per-Seat Pricing", "Zero-Risk Handover"]}
        onAction={handleActionWrapper}
        showBackground={true}
        backgroundVariant="grid-blobs"
        backgroundIntensity="bold"
        accent="emerald"
        showTopStripe={true}
        className="border-t border-slate-200"
      />

    </div>
  );
};